/**
 * Telas do Watchdog (decisões 189 a 191 e 244 a 248), no visual das telas de conta (AuthLayout,
 * "Elements"), alinhadas com a proposta B da Conta (25/10/2026): os mesmos nomes ("Chrome no
 * Windows"), os mesmos horários ("Pedido às 14:02 · vale até 14:32"), o caminho Conta › Licença ›
 * Computadores e a linha de licença individual no fim de cada tela.
 *  - Autorize este computador: pelo link do email (mascarado) ou no computador que está com a
 *    licença; espera a autorização sozinha (a consulta roda em useWatchdog);
 *  - Pedido recusado / O pedido venceu: "Pedir de novo";
 *  - Em uso em outro computador: "Usar neste computador" (pausa o outro). Da segunda troca do dia
 *    em diante, a tela diz a partir de quando dá para usar aqui e o botão fica desligado até lá.
 * Mais as duas janelas sobre o painel: "Você abriu em outro computador" (este perdeu a vez com o
 * painel aberto) e o pedido de acesso no computador que está com a licença.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AuthLayout, Html, ScreenDialog, StateIcon } from '../../components/screen/Screen';
import { useSessionStore } from '../../stores/sessionStore';
import {
  useWatchdogStore,
  type SwitchWait,
  type WatchdogPhase,
} from '../../stores/watchdogStore';
import { showDeleteToast } from '../lists/deleteToast';
import { deviceIcon, escapeText, maskEmail, useAccountText } from './accountText';
import { useWatchdogActions } from './useWatchdog';
import './account.css';

function Waiting({ text }: { text: string }): JSX.Element {
  return (
    <p className="as-pulse" role="status">
      <span className="as-pulse__giro ds-giro material-symbols-rounded" aria-hidden="true">
        progress_activity
      </span>
      {text}
    </p>
  );
}

/** Linha de licença individual no fim de todas as telas que cobrem o painel. */
function Individual(): JSX.Element {
  const { tx } = useAccountText();
  return <p className="acc-individual">{tx('wd_individual')}</p>;
}

/** A espera da troca enquanto não passa; ao passar, "Usar neste computador" volta a valer. */
function useSwitchWait(): SwitchWait | null {
  const wait = useWatchdogStore((s) => s.wait);
  const [, setTick] = useState(0);
  const until = wait ? new Date(wait.availableAt).getTime() : 0;
  useEffect(() => {
    const left = until - Date.now();
    if (left <= 0) return undefined;
    // setTimeout aceita no máximo ~24,8 dias; a espera é de horas.
    const id = window.setTimeout(() => setTick((n) => n + 1), Math.min(left + 500, 2 ** 31 - 1));
    return () => window.clearTimeout(id);
  }, [until]);
  return wait && until > Date.now() ? wait : null;
}

/** "A licença já trocou de computador hoje. Você poderá usar aqui a partir de amanhã, 06:10…" */
function useWaitText(): (wait: SwitchWait) => string {
  const { tx, when, deviceName } = useAccountText();
  return (wait) =>
    tx('paused_wait', {
      when: when(wait.availableAt),
      hours: wait.waitHours,
      name: deviceName(wait.lastUseDevice),
    });
}

function AuthorizeScreen(): JSX.Element {
  const { tx, time } = useAccountText();
  const { askAgain } = useWatchdogActions();
  const email = useSessionStore((s) => s.session?.email ?? '');
  const request = useWatchdogStore((s) => s.request);
  const emailSent = useWatchdogStore((s) => s.emailSent);
  const [note, setNote] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function resend(): Promise<void> {
    setBusy(true);
    const code = await askAgain();
    setBusy(false);
    setNote(
      code === 'rate_limited' ? tx('auth_rate') : code ? tx('toast_error') : tx('auth_resent'),
    );
  }

  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="devices" tone="gray" />
        <div className="as-head">
          <h1 id="as-title">{tx('auth_title')}</h1>
          <p className="as-card__text">{tx('auth_text')}</p>
        </div>
        <ul className="acc-ways">
          <li className="acc-way">
            <span className="material-symbols-rounded" aria-hidden="true">
              mail
            </span>
            <span className="acc-way__text">
              {emailSent === false
                ? tx('auth_email_failed')
                : tx('auth_by_email', { email: maskEmail(email) })}
            </span>
          </li>
          <li className="acc-way">
            <span className="material-symbols-rounded" aria-hidden="true">
              computer
            </span>
            <span className="acc-way__text">{tx('auth_by_device')}</span>
          </li>
        </ul>
        <Waiting
          text={tx('auth_waiting', { until: request?.expiresAt ? time(request.expiresAt) : '—' })}
        />
        <div className="acc-gate__acao">
          <button
            type="button"
            className="as-btn as-btn--outline as-btn--md ds-resposta"
            disabled={busy}
            onClick={() => void resend()}
          >
            {tx('auth_resend')}
          </button>
          {note ? (
            <span className="as-card__text" role="status" aria-live="polite">
              {note}
            </span>
          ) : null}
        </div>
        <Individual />
      </section>
    </AuthLayout>
  );
}

function AskAgainScreen({ kind }: { kind: 'refused' | 'expired' }): JSX.Element {
  const { tx, time } = useAccountText();
  const { askAgain } = useWatchdogActions();
  const request = useWatchdogStore((s) => s.request);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  async function again(): Promise<void> {
    setBusy(true);
    const code = await askAgain();
    setBusy(false);
    if (code) setNote(code === 'rate_limited' ? tx('auth_rate') : tx('toast_error'));
  }

  const text =
    kind === 'refused'
      ? tx('refused_text')
      : request?.expiresAt
        ? tx('expired_text', { until: time(request.expiresAt) })
        : null;

  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon={kind === 'refused' ? 'block' : 'schedule'} tone="gray" />
        <div className="as-head">
          <h1 id="as-title">{tx(kind === 'refused' ? 'refused_title' : 'expired_title')}</h1>
          {text ? <p className="as-card__text">{text}</p> : null}
        </div>
        <button
          type="button"
          className="as-btn as-btn--primary as-btn--block ds-resposta"
          disabled={busy}
          onClick={() => void again()}
        >
          {tx('ask_again')}
        </button>
        {note ? (
          <p className="as-card__text" role="status">
            {note}
          </p>
        ) : null}
        <Individual />
      </section>
    </AuthLayout>
  );
}

function PausedScreen(): JSX.Element {
  const { tx, deviceName } = useAccountText();
  const { claim } = useWatchdogActions();
  const activeDevice = useWatchdogStore((s) => s.activeDevice);
  const wait = useSwitchWait();
  const waitText = useWaitText();
  const [busy, setBusy] = useState(false);
  const name = deviceName(activeDevice);

  async function takeTurn(): Promise<void> {
    setBusy(true);
    await claim();
    setBusy(false);
  }

  return (
    <AuthLayout>
      <section className="as-card" aria-labelledby="as-title">
        <StateIcon icon="devices_other" tone="gray" />
        <div className="as-head">
          <h1 id="as-title">{tx('paused_title')}</h1>
          <p className="as-card__text">
            {wait ? waitText(wait) : tx('paused_text', { name })}
          </p>
        </div>
        <button
          type="button"
          className="as-btn as-btn--primary as-btn--block ds-resposta"
          disabled={busy || wait !== null}
          onClick={() => void takeTurn()}
        >
          {tx('use_here')}
        </button>
        {wait ? null : (
          <p className="as-card__text acc-gate__nota">{tx('paused_note', { name })}</p>
        )}
        <Individual />
      </section>
    </AuthLayout>
  );
}

/** Tela inteira enquanto este computador não pode usar o painel. */
export function WatchdogGate({ phase }: { phase: WatchdogPhase }): JSX.Element | null {
  if (phase === 'authorize') return <AuthorizeScreen />;
  if (phase === 'refused') return <AskAgainScreen kind="refused" />;
  if (phase === 'expired') return <AskAgainScreen kind="expired" />;
  if (phase === 'paused') return <PausedScreen />;
  return null;
}

/** Outro computador pegou a vez com o painel aberto aqui: janela por cima, sem fechar. */
export function SwitchedDialog(): JSX.Element {
  const { tx, deviceName } = useAccountText();
  const { claim } = useWatchdogActions();
  const activeDevice = useWatchdogStore((s) => s.activeDevice);
  const wait = useSwitchWait();
  const waitText = useWaitText();
  const [busy, setBusy] = useState(false);

  async function takeTurn(): Promise<void> {
    setBusy(true);
    await claim();
    setBusy(false);
  }

  return (
    <ScreenDialog
      icon="devices_other"
      iconTone="gray"
      title={tx('switched_title')}
      closeLabel={tx('cancel')}
    >
      <Html
        as="div"
        className="as-dialog__text"
        html={tx('switched_text', { device: escapeText(deviceName(activeDevice)) })}
      />
      {wait ? <p className="as-dialog__text">{waitText(wait)}</p> : null}
      <div className="as-dialog__actions">
        <button
          type="button"
          className="as-btn as-btn--primary as-btn--md ds-resposta"
          disabled={busy || wait !== null}
          onClick={() => void takeTurn()}
        >
          {tx('use_here')}
        </button>
      </div>
    </ScreenDialog>
  );
}

/**
 * Pedido de acesso chegando no computador que está com a licença (proposta B, 25/10/2026).
 * "Ver depois" fecha; o pedido continua no selo de Conta na barra, no item Licença do menu e no
 * topo de Conta › Licença › Computadores, onde a janela não aparece. Os detalhes do computador
 * que pediu (decisão 247) ajudam a reconhecê-lo.
 */
export function RequestNotice(): JSX.Element | null {
  const { tx, time, deviceName, detailsLine } = useAccountText();
  const { decide } = useWatchdogActions();
  const location = useLocation();
  const pending = useWatchdogStore((s) => s.pending);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  const request = pending.find((item) => !dismissed.includes(item.id));
  const onLicense = location.pathname === '/account';

  if (!request || onLicense) return null;

  const name = deviceName(request);
  const details = detailsLine(request);
  const later = (): void => setDismissed((list) => [...list, request.id]);

  async function onDecide(decision: 'approve' | 'refuse'): Promise<void> {
    if (!request || busy) return;
    setBusy(true);
    const code = await decide(request.id, decision);
    setBusy(false);
    if (code) {
      later();
      showDeleteToast(tx('toast_error'), 'error');
      return;
    }
    showDeleteToast(decision === 'approve' ? tx('toast_approved', { name }) : tx('toast_refused'));
  }

  return (
    <ScreenDialog
      icon={deviceIcon(request.os)}
      iconTone="gray"
      title={tx('notice_title')}
      closeLabel={tx('notice_later')}
      className="acc-janela"
      onClose={later}
    >
      <p className="as-dialog__text">
        {tx('notice_text', { name })}
        {details ? <span className="acc-dialogo__apoio">{details}</span> : null}
        {request.createdAt && request.expiresAt ? (
          <span className="acc-dialogo__apoio">
            {tx('req_when', { time: time(request.createdAt), until: time(request.expiresAt) })}
          </span>
        ) : null}
      </p>
      <p className="acc-nota">{tx('link_individual')}</p>
      <div className="as-dialog__actions acc-dialogo__acoes">
        <button
          type="button"
          className="as-btn as-btn--md acc-btn--fantasma ds-resposta"
          onClick={later}
        >
          {tx('notice_later')}
        </button>
        <span className="as-grow" />
        <button
          type="button"
          className="as-btn as-btn--outline as-btn--md ds-resposta"
          disabled={busy}
          onClick={() => void onDecide('refuse')}
        >
          {tx('req_refuse')}
        </button>
        <button
          type="button"
          className="as-btn as-btn--dark as-btn--md ds-resposta"
          disabled={busy}
          onClick={() => void onDecide('approve')}
        >
          {tx('req_approve')}
        </button>
      </div>
    </ScreenDialog>
  );
}
